import { AbstractComponent } from "appworks/components/abstract-component";
import { GraphicsService } from "appworks/graphics/graphics-service";
import { Container } from "appworks/graphics/pixi/container";
import { CenterPivot, PIXIElement } from "appworks/graphics/pixi/group";
import { Sprite } from "appworks/graphics/pixi/sprite";
import { Services } from "appworks/services/services";
import { TranslationsService } from "appworks/services/translations/translations-service";
import { Contract } from "appworks/utils/contracts/contract";
import { Parallel } from "appworks/utils/contracts/parallel";
import { Sequence } from "appworks/utils/contracts/sequence";
import { Point } from "appworks/utils/geom/point";
import { RandomBoolean, RandomFromArray, RandomRangeFloat, RandomRangeInt } from "appworks/utils/math/random";
import { Easing, Tween } from "appworks/utils/tween";
import { gameLayers } from "game-layers";
import { Graphics } from "pixi.js";
import { FlingBonusPrizeTier, FlingBonusPrizeValues, FowlFlingBonusComponent } from "./fowl-fling-bonus-component";

export class FowlFlingBonusBackgroundComponent extends AbstractComponent {
    protected static MAX_HEIGHT = 1080 * 12;
    protected static SECTOR_BAR_GAP = 15;
    protected static SECTOR_BAR_HEIGHT = 20;
    protected static FIELD_X_OFFSET = -30; // to line up the border with the fence gaps

    protected bgLayer = gameLayers.FlingBonusBackground;
    protected fgLayer = gameLayers.FlingBonusForeground;

    protected scroll: Point = new Point();

    protected marker: Sprite;

    protected fieldSprites: Sprite[];
    protected cloudSprites: Sprite[];
    protected mountainSprites: Sprite[];

    protected sectors: FlingBonusPrizeTier[];
    protected sectorGraphic: Graphics;

    protected starContainer: Container;

    protected fenceSprites: Map<FlingBonusPrizeTier, Sprite[]>;

    protected sectorColors: Map<FlingBonusPrizeTier, number> = new Map([
        [FlingBonusPrizeTier.MINI, 0xf06600],
        [FlingBonusPrizeTier.MINOR, 0xffe300],
        [FlingBonusPrizeTier.MAJOR, 0x3dae00],
        [FlingBonusPrizeTier.MEGA, 0x7bdfff],
        [FlingBonusPrizeTier.GRAND, 0x9b59ff],
        [FlingBonusPrizeTier.START, 0xffffff],
    ]);

    public show(): Contract {
        return new Sequence([
            () => new Parallel([() => this.bgLayer.setScene("bonus"), () => this.fgLayer.setScene("bonus")]),
            () =>
                Contract.wrap(() => {
                    this.setupBackgroundGradient();

                    this.setupStars();
                    this.setupCloudSprites();
                    this.setupMountainSprites();
                    this.setupFieldSprites();

                    this.generateRandomSectors();
                    this.setupFenceSprites();

                    this.sectorGraphic = new Graphics();
                    this.fgLayer.add(this.sectorGraphic);

                    this.marker = this.fgLayer.getSprite("marker");
                    CenterPivot(this.marker, null, new Point(0.5, 1));
                    this.showMarker(false, 0).execute();
                }),
        ]);
    }

    public hide(): Contract {
        return new Sequence([
            () => new Parallel([() => this.bgLayer.defaultScene(), () => this.fgLayer.defaultScene()]),
            () =>
                Contract.wrap(() => {
                    this.fieldSprites = [];
                    this.fenceSprites.clear();
                    this.cloudSprites = [];
                    this.mountainSprites = [];
                    this.sectors = [];
                    this.scroll.set(0, 0);
                }),
        ]);
    }

    public showMarker(show: boolean, duration: number): Contract {
        return new Contract(resolve => {
            new Tween(this.marker.landscape.scale)
                .to({ x: show ? 1 : 0, y: show ? 1 : 0 }, duration)
                .easing(Easing.Elastic.Out)
                .onUpdate(() => this.marker.portrait.scale.set(this.marker.landscape.scale.x))
                .onComplete(resolve)
                .start();
        });
    }

    public setScroll(scroll: Point) {
        const delta = scroll.subtract(this.scroll);
        this.scroll.set(scroll.x, scroll.y);

        this.updateFencesAndSectorBar();

        // Update background Y pos
        gameLayers.FlingBonusBackground.container.landscape.set(0, this.scroll.y);
        gameLayers.FlingBonusBackground.container.portrait.set(0, this.scroll.y);

        // Stars
        this.starContainer.landscape.x = this.bgLayer.bounds.landscape.width * (scroll.x / FowlFlingBonusComponent.MAX_DISTANCE);
        this.starContainer.landscape.y = this.bgLayer.bounds.landscape.height * (scroll.y / FowlFlingBonusBackgroundComponent.MAX_HEIGHT) - scroll.y;
        this.starContainer.portrait.setPosition(this.starContainer.landscape);
        this.starContainer.alpha = (scroll.y + this.bgLayer.bounds.landscape.height) / (FowlFlingBonusBackgroundComponent.MAX_HEIGHT / 2) - 0.5;

        // Scroll clouds
        this.cloudSprites.forEach(sprite => {
            sprite.landscape.x += delta.x;
            if (sprite.landscape.x < -sprite.landscape.width) {
                this.resetCloudSpritePosition(sprite, true);
            }
            sprite.portrait.x = sprite.landscape.x;
        });

        // Scroll mountains
        this.mountainSprites.forEach((sprite, index) => {
            sprite.landscape.x = sprite.landscape.width * index + this.scroll.x;
            while (sprite.landscape.x < -sprite.landscape.width) {
                sprite.landscape.x += sprite.landscape.width * this.mountainSprites.length;
            }
            sprite.portrait.x = sprite.landscape.x;
        });

        // Scroll field
        this.fieldSprites.forEach((sprite, index) => {
            sprite.landscape.x = sprite.landscape.width * index + this.scroll.x + FowlFlingBonusBackgroundComponent.FIELD_X_OFFSET;
            while (sprite.landscape.x < -sprite.landscape.width) {
                sprite.landscape.x += sprite.landscape.width * this.fieldSprites.length;
            }
            sprite.portrait.x = sprite.landscape.x;
        });
    }

    public setPrize(prize: FlingBonusPrizeTier, landX: number) {
        const landingTileIndex = Math.floor(landX / FowlFlingBonusComponent.SECTOR_WIDTH);
        this.sectors[landingTileIndex] = prize;
    }

    public updatePositionMarker(foxPosX: number) {
        this.marker.landscape.x = foxPosX + this.scroll.x;
        this.marker.landscape.y = this.fgLayer.bounds.landscape.height - FowlFlingBonusBackgroundComponent.SECTOR_BAR_HEIGHT;

        this.marker.portrait.setPosition(this.marker.landscape);
    }

    protected setupBackgroundGradient() {
        const width = 1920;
        const height = FowlFlingBonusBackgroundComponent.MAX_HEIGHT;
        const scale = 10; // otherwise it's too big for phones to render

        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height / scale;

        const context = canvas.getContext("2d");
        const gradient = context.createLinearGradient(0, 0, 0, height / scale);

        gradient.addColorStop(1 - 0.0, "#d9ffd8");
        gradient.addColorStop(1 - 0.05, "#98fff9");
        gradient.addColorStop(1 - 0.2, "#2a49fa");
        gradient.addColorStop(1 - 0.45, "#110066");
        gradient.addColorStop(1 - 1.0, "#10002b");
        context.fillStyle = gradient;
        context.fillRect(0, 0, width, height / scale);

        const sprite = Sprite.from(canvas);
        this.bgLayer.add(sprite);

        CenterPivot(sprite, null, new Point(0, 1));
        sprite.landscape.y = this.bgLayer.bounds.landscape.height;
        sprite.portrait.y = this.bgLayer.bounds.portrait.height;
        sprite.landscape.scale.y = scale;
        sprite.portrait.scale.y = scale;
    }

    protected setupStars() {
        this.starContainer = new Container();
        this.starContainer.alpha = 0;
        this.bgLayer.add(this.starContainer);

        const starTints = [0xffffff, 0xebb991, 0xcd8ff8, 0xad8df6];
        const graphicsService = Services.get(GraphicsService);
        for (let numStars = 0; numStars < 60; numStars++) {
            const star = graphicsService.createSprite("flingbonus/star_small");
            this.starContainer.addChild(star);

            star.tint = RandomFromArray(starTints);
            star.alpha = RandomRangeFloat(0.25, 1);

            const scale = RandomRangeFloat(0.25, 1);
            star.landscape.scale.set(scale);
            star.portrait.scale.set(scale);

            star.anchor.set(0.5);
            star.rotation = Math.PI * 2 * Math.random();

            star.landscape.x = RandomRangeInt(0, this.bgLayer.bounds.landscape.width * 2);
            star.landscape.y = RandomRangeInt(0, this.bgLayer.bounds.landscape.height * 2);
        }

        for (let numBigStars = 0; numBigStars < 5; numBigStars++) {
            const bigStar = graphicsService.createSprite("flingbonus/space_star_" + RandomRangeInt(1, 2));
            this.starContainer.addChild(bigStar);

            const scale = RandomRangeFloat(0.5, 1);
            bigStar.landscape.scale.set(scale);
            bigStar.portrait.scale.set(scale);

            bigStar.landscape.x = RandomRangeInt(0, this.bgLayer.bounds.landscape.width * 2);
            bigStar.landscape.y = RandomRangeInt(0, this.bgLayer.bounds.landscape.height * 2);
        }

        for (let numPlanets = 0; numPlanets < 4; numPlanets++) {
            const planet = graphicsService.createSprite("flingbonus/planet_" + RandomRangeInt(1, 4));
            this.starContainer.addChild(planet);

            const scale = RandomRangeFloat(0.5, 1);
            planet.landscape.scale.set(scale);
            planet.portrait.scale.set(scale);

            planet.landscape.x = RandomRangeInt(0, this.bgLayer.bounds.landscape.width * 2);
            planet.landscape.y = RandomRangeInt(0, this.bgLayer.bounds.landscape.height / 2); // only in uppermost bit
        }

        this.starContainer.children.forEach((child: PIXIElement) => {
            child.landscape.y -= this.bgLayer.bounds.landscape.height;
            child.portrait.setPosition(child.landscape);
        });
    }

    protected setupFieldSprites() {
        this.fieldSprites = [
            Services.get(GraphicsService).createSprite("flingbonus/bg_field_1"),
            Services.get(GraphicsService).createSprite("flingbonus/bg_field_2"),
            Services.get(GraphicsService).createSprite("flingbonus/bg_field_1"),
            Services.get(GraphicsService).createSprite("flingbonus/bg_field_2"),
        ];
        this.fieldSprites.forEach(sprite => {
            this.bgLayer.add(sprite);

            sprite.landscape.y = this.bgLayer.bounds.landscape.height - sprite.landscape.height;
            sprite.portrait.y = this.bgLayer.bounds.portrait.height - sprite.portrait.height;
        });
    }

    protected generateRandomSectors() {
        this.sectors = [FlingBonusPrizeTier.START];

        const weightedPrizeTiers = [
            FlingBonusPrizeTier.MINI,
            FlingBonusPrizeTier.MINI,
            FlingBonusPrizeTier.MINOR,
            FlingBonusPrizeTier.MINOR,
            FlingBonusPrizeTier.MAJOR,
            FlingBonusPrizeTier.MAJOR,
            FlingBonusPrizeTier.MEGA,
            FlingBonusPrizeTier.GRAND,
        ];

        while (this.sectors.length * FowlFlingBonusComponent.SECTOR_WIDTH < FowlFlingBonusComponent.MAX_DISTANCE) {
            this.sectors.push(RandomFromArray(weightedPrizeTiers));
        }
    }

    protected setupFenceSprites() {
        this.fenceSprites = new Map([
            [FlingBonusPrizeTier.START, []],
            [FlingBonusPrizeTier.MINI, []],
            [FlingBonusPrizeTier.MINOR, []],
            [FlingBonusPrizeTier.MAJOR, []],
            [FlingBonusPrizeTier.MEGA, []],
            [FlingBonusPrizeTier.GRAND, []],
        ]);

        const graphicsService = Services.get(GraphicsService);
        const i18nService = Services.get(TranslationsService);

        // 3 sprites for each tier, it's the max you'd ever see
        this.fenceSprites.forEach((arr: Sprite[], tier: FlingBonusPrizeTier) => {
            for (let i = 0; i < 3; i++) {
                const fence = graphicsService.createSprite("flingbonus/fence_" + tier);
                fence.landscape.y = this.bgLayer.bounds.landscape.height - fence.landscape.height;
                fence.portrait.y = this.bgLayer.bounds.portrait.height - fence.portrait.height;
                this.bgLayer.add(fence);
                arr.push(fence);

                if (FlingBonusPrizeValues[tier]) {
                    const xPositions = [60, 985];
                    xPositions.forEach(xPos => {
                        const text = this.bgLayer.getText("tier_text").clone();
                        CenterPivot(text).angle = -1.2;
                        text.text = `${i18nService.get(tier).toLocaleUpperCase()}  x${FlingBonusPrizeValues[tier]}`;
                        text.landscape.set(xPos, 43);
                        text.portrait.setPosition(text.landscape);
                        fence.addChild(text);
                    });
                }
            }
        });
    }

    protected setupMountainSprites() {
        const graphicsService = Services.get(GraphicsService);
        this.mountainSprites = [];

        for (let numMountains = 0; numMountains < 10; numMountains++) {
            const sprite = graphicsService.createSprite("flingbonus/mountains_" + RandomRangeInt(1, 3));
            this.bgLayer.add(sprite);
            this.mountainSprites.push(sprite);
            CenterPivot(sprite, null, new Point(0, 1));

            sprite.landscape.y = this.bgLayer.bounds.landscape.height - 640;
            sprite.portrait.y = this.bgLayer.bounds.portrait.height - 640;
        }
    }

    protected setupCloudSprites() {
        this.cloudSprites = [];
        const graphicsService = Services.get(GraphicsService);

        for (let numClouds = 0; numClouds < 30; numClouds++) {
            const sprite = graphicsService.createSprite("flingbonus/cloud");
            this.bgLayer.add(sprite);
            this.cloudSprites.push(sprite);
            this.resetCloudSpritePosition(sprite, RandomBoolean());
        }
    }

    protected resetCloudSpritePosition(sprite: Sprite, offScreen: boolean) {
        sprite.alpha = RandomRangeFloat(0.2, 0.8);

        const scale = RandomRangeFloat(0.2, 1.25);
        sprite.landscape.scale.set(scale);
        sprite.portrait.scale.set(scale);

        const xPos = this.bgLayer.bounds.landscape.width * Math.random() + (offScreen ? this.bgLayer.bounds.landscape.width : 0);
        sprite.landscape.x = xPos;
        sprite.portrait.x = xPos;

        const yPos = RandomRangeInt(1100, 6500);
        sprite.landscape.y = this.bgLayer.bounds.landscape.y + this.bgLayer.bounds.landscape.height - yPos;
        sprite.portrait.y = this.bgLayer.bounds.portrait.y + this.bgLayer.bounds.portrait.height - yPos;
    }

    protected updateFencesAndSectorBar() {
        this.fenceSprites.forEach(sprites => sprites.forEach(sprite => (sprite.visible = false)));
        this.sectorGraphic.clear();

        const sectorWidth = FowlFlingBonusComponent.SECTOR_WIDTH;
        const barHeight = FowlFlingBonusBackgroundComponent.SECTOR_BAR_HEIGHT;
        const barGap = FowlFlingBonusBackgroundComponent.SECTOR_BAR_GAP;

        const startIndex = Math.floor(Math.abs(this.scroll.x) / sectorWidth);
        const offset = this.scroll.x % sectorWidth;

        for (let i = 0; i < 3; i++) {
            const tier = this.sectors[startIndex + i];
            const color = this.sectorColors.get(tier);

            // fence sprite
            const sprite = this.fenceSprites.get(tier)[i];
            sprite.visible = true;
            sprite.landscape.x = sectorWidth * (startIndex + i) + this.scroll.x;
            sprite.portrait.x = sectorWidth * (startIndex + i) + this.scroll.x;

            // sector bar
            this.sectorGraphic.beginFill(color);
            this.sectorGraphic.drawRect(
                i * sectorWidth + offset + barGap / 2,
                this.fgLayer.bounds.landscape.height - barHeight,
                sectorWidth - barGap,
                barHeight
            );
        }
    }
}
